import { attrs, cx, html, type Html, type Part } from "./html.ts";
import { brandImg } from "./assets.ts";
import { icon, type IconName } from "./icons.ts";
import { statusPill, type StatusPillProps } from "./components/run.ts";

/**
 * App shell, ported from the design's `.app`, `.nav`, `.top` and `.stat`: a 232px left nav (logo, Runs, Files,
 * History, Agents, repo list, user) that collapses to a 64px icon rail on the live run, and a 60px top bar with
 * breadcrumb, run status pill and live stats in data mono.
 */
export type NavSection = "runs" | "files" | "history" | "agents";

const SECTIONS: ReadonlyArray<{ id: NavSection; label: string }> = [
  { id: "runs", label: "Runs" },
  { id: "files", label: "Files" },
  { id: "history", label: "History" },
  { id: "agents", label: "Agents" },
];

export interface NavRepo {
  name: string;
  href?: string;
  active?: boolean;
}

export interface NavProps {
  active?: NavSection;
  hrefs?: Partial<Record<NavSection, string>>;
  repos?: readonly NavRepo[];
  /** First name only. Humans are named by first name. */
  user: { firstName: string; initials: string; settingsHref?: string };
  /** The 64px icon rail used on the live run. */
  rail?: boolean;
}

export function nav(props: NavProps): Html {
  const items = SECTIONS.map((s) => {
    const current = props.active === s.id;
    const common = { class: cx("nav__item", current && "is-on"), href: props.hrefs?.[s.id] ?? "#", "aria-current": current ? "page" : undefined };
    return props.rail
      ? html`<a${attrs({ ...common, "aria-label": s.label })}>${icon(s.id satisfies IconName, 20)}</a>`
      : html`<a${attrs(common)}>${icon(s.id satisfies IconName)}${s.label}</a>`;
  });
  if (props.rail) {
    return html`<nav class="nav ap-rail" aria-label="Main">${brandImg("mark", { alt: "Botallion" })}${items}<div class="spacer"></div><span class="avatar-h" role="img" aria-label="${props.user.firstName}">${props.user.initials}</span></nav>`;
  }
  const repos = props.repos?.length
    ? html`<span class="bt-eyebrow nav__sec">Repos</span>${props.repos.map((r) => html`<a${attrs({ class: "nav__item", href: r.href ?? "#", "aria-current": r.active ? "page" : undefined, style: r.active ? "color: var(--ink);" : undefined })}><span class="bt-mono" style="font-size: 13px;">${r.name}</span></a>`)}`
    : "";
  return html`<nav class="nav" aria-label="Main"><div class="nav__logo">${brandImg("mark", { style: "width: 30px; height: 25px;" })}Botallion</div>${items}${repos}<div class="nav__foot"><span class="avatar-h" aria-hidden="true">${props.user.initials}</span>${props.user.firstName}<div class="spacer"></div><a${attrs({ href: props.user.settingsHref ?? "#", "aria-label": "Settings", style: "color: var(--ink-muted); display: grid;" })}>${icon("settings")}</a></div></nav>`;
}

export interface StatProps {
  label: string;
  /** Live numbers are mono and tabular so they do not jitter. */
  value: Part;
}

export function stat(props: StatProps): Html {
  return html`<div class="stat"><span class="stat__v">${props.value}</span><span class="stat__k">${props.label}</span></div>`;
}

export interface TopBarProps {
  repo?: string;
  /** Replaces the repo crumb, e.g. a link back to the run. Include its trailing slash. */
  crumb?: Part;
  title: Part;
  status?: StatusPillProps;
  stats?: readonly StatProps[];
  /** Pause, Stop run and the like, right of the stats. */
  actions?: Part;
}

export function topBar(props: TopBarProps): Html {
  return html`<header class="top"><div class="top__title">${props.crumb ?? (props.repo !== undefined ? html`<span class="crumb">${props.repo} /</span>` : "")}${props.title}</div>${props.status ? statusPill(props.status) : ""}<div class="spacer"></div>${props.stats?.map(stat) ?? ""}${props.actions ?? ""}</header>`;
}

export interface FrameSize {
  width: number;
  height: number;
}

export interface AppShellProps {
  nav: NavProps;
  top: TopBarProps;
  /** The body under the top bar. It brings its own panes. */
  main: Part;
  theme?: "dark" | "light" | undefined;
  /** Fix the size, for the review harness. Omit in the app: it fills the viewport. */
  frame?: FrameSize | undefined;
}

const frameStyle = (frame: FrameSize | undefined): string | undefined => (frame ? `width: ${frame.width}px; height: ${frame.height}px;` : undefined);

/** The desktop app frame: nav (or rail), top bar, body. */
export function appShell(props: AppShellProps): Html {
  return html`<div${attrs({ class: cx("scr", !props.frame && "ap-fill"), "data-theme": props.theme ?? "dark", style: frameStyle(props.frame) })}><div class="app">${nav(props.nav)}<div class="main">${topBar(props.top)}${props.main}</div></div></div>`;
}

/** Centred flow root (sign up, invite, onboarding). */
export function flowShell(props: { theme?: "dark" | "light" | undefined; frame?: FrameSize | undefined; children: Part }): Html {
  return html`<div${attrs({ class: cx("scr flow", !props.frame && "ap-fill"), "data-theme": props.theme ?? "dark", style: frameStyle(props.frame) })}>${props.children}</div>`;
}

export interface PhoneShellProps {
  theme?: "dark" | "light" | undefined;
  frame?: boolean;
  /** Top bar content: back link or logo, the title block and a pill. */
  top: Part;
  body: Part;
  /** The sticky bottom bar: primary actions at control-lg (44px). */
  bar?: Part | undefined;
  barClass?: string | undefined;
}

export function phoneShell(props: PhoneShellProps): Html {
  return html`<div class="scr phone" data-theme="${props.theme ?? "dark"}"><div class="phone__top">${props.top}</div><div class="phone__body">${props.body}</div>${props.bar !== undefined ? html`<div class="${cx("phone__bar", props.barClass)}">${props.bar}</div>` : ""}</div>`;
}

/** Wrap a desktop and a phone rendering so CSS shows the right one (phones start at 640px). */
export function responsive(desktop: Part, phone: Part): Html {
  return html`<div class="ap-desktop">${desktop}</div><div class="ap-phone">${phone}</div>`;
}
